import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';
import { mat3, mat4 } from 'gl-matrix';
import type { AnalysisType, VOI } from 'config/analysisConfig';
import type { AppConfig } from 'config/config';
import type { ImageLayerKey } from '../cprImageResources';
import type { ImageWindowingPolicy } from '../imageWindowing';
import { publishImageLayerStackLiveWindowingPolicy } from '../ImageLayerStack';
import {
  calculateImageFitRect,
  type ViewportTransform,
} from '../cprImageLayout';

type RgbaColor = [number, number, number, number];
type Point2D = [number, number];

type ServerCrossSectionCanvasProps = {
  className?: string;
  imageData: vtkImageData | null | undefined;
  labelData: vtkImageData | null | undefined;
  axes: mat4 | undefined;
  index: number;
  analysisType: AnalysisType;
  voi: VOI;
  config: AppConfig;
  baseWidth: number;
  upscale: number;
  activeToolId?: string;
  visibility?: Partial<Record<ImageLayerKey, boolean>>;
  windowingPolicy?: ImageWindowingPolicy;
  onWheelIndex?: (delta: number) => void;
  onScrollDragIndex?: (delta: number) => void;
  onWindowLevelChange?: (next: ImageWindowingPolicy) => void;
  rendererId?: string;
  initialZoomScale?: number;
  slabThicknessMm?: number;
  onHoverHU?: (hu: number | null) => void;
};

type SliceResult = {
  width: number;
  height: number;
  rgba: Uint8ClampedArray;
  labels: Uint8Array;
  huValues: Float32Array;
  sampleScale: number;
  requestedWidth: number;
  capped: boolean;
};

type OutlinePathData = {
  width: number;
  height: number;
  lumen: string[];
  plaque: string[];
};

type DragKind = 'pan' | 'zoom' | 'scroll' | 'window-level';

type ImageGeometry = {
  width: number;
  height: number;
};

const EMPTY_GEOMETRY: ImageGeometry = { width: 1, height: 1 };

// Initial server-canvas cross-section zoom. Increase this value if the first
// view should open more zoomed-in. 1.0 means pure contain-fit, 1.45 is a
// moderate VTK-like initial enlargement.
const SERVER_CROSS_SECTION_INITIAL_ZOOM_SCALE = 3.0;
const SERVER_CROSS_SECTION_AVC_INITIAL_ZOOM_SCALE = 1.5;

// Server canvas CrossSection CPU reslice output limit.
// 512 keeps the visible CrossSection sharp enough for the current panel size while
// cutting per-slice CPU sampling work to 1/4 of the previous 1024 x 1024 path.
// Increase to 768 or 1024 if you need more detail, decrease to 384 for weaker PCs.
const SERVER_CROSS_SECTION_MAX_RENDER_SIZE = 256;

const SERVER_CROSS_SECTION_PERF_LOG_THRESHOLD_MS = 16;
const SERVER_CROSS_SECTION_OUTLINE_SMOOTHING_ITERATIONS = 2;
const SERVER_CROSS_SECTION_OUTLINE_MIN_CONTOUR_POINTS = 4;
// SVG outline stroke width in screen pixels. Keep this low because the server
// canvas uses a small 256 px reslice image and the outline is intentionally
// drawn as a non-scaling overlay.
const SERVER_CROSS_SECTION_OUTLINE_STROKE_WIDTH = 0.5;
const SERVER_CROSS_SECTION_SLAB_EPSILON_MM = 1e-6;

function logServerCrossSectionPerf(
  _label: string,
  _payload: Record<string, number | string | boolean | undefined>,
) {}

// Mouse wheel index speed for server-canvas cross-section.
// Increase WHEEL_INDEX_SENSITIVITY for faster index changes per wheel notch.
const SERVER_CROSS_SECTION_WHEEL_INDEX_SENSITIVITY = 3;
const SERVER_CROSS_SECTION_WHEEL_DELTA_PER_INDEX = 80;
const SERVER_CROSS_SECTION_MAX_WHEEL_INDEX_STEP = 12;

function getWheelIndexStep(deltaY: number) {
  if (!Number.isFinite(deltaY) || deltaY === 0) return 0;
  const direction = deltaY > 0 ? 1 : -1;
  const baseStep = Math.max(
    1,
    Math.round(
      Math.abs(deltaY) / SERVER_CROSS_SECTION_WHEEL_DELTA_PER_INDEX,
    ),
  );
  return (
    direction *
    Math.min(
      SERVER_CROSS_SECTION_MAX_WHEEL_INDEX_STEP,
      baseStep * SERVER_CROSS_SECTION_WHEEL_INDEX_SENSITIVITY,
    )
  );
}

function clampViewportScale(scale: number) {
  if (!Number.isFinite(scale)) return 1;
  return Math.max(0.1, Math.min(24, scale));
}
function getInitialViewportScale(analysisType: AnalysisType) {
  return analysisType === 'aorticValveCalcium' || analysisType === 'tavi'
    ? SERVER_CROSS_SECTION_AVC_INITIAL_ZOOM_SCALE
    : SERVER_CROSS_SECTION_INITIAL_ZOOM_SCALE;
}

function nextFrame() {
  return new Promise<void>((resolve) => {
    requestAnimationFrame(() => resolve());
  });
}

async function loadSvgDataUrl(svgText: string) {
  const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgText)}`;
  const image = new Image();
  const loaded = new Promise<HTMLImageElement>((resolve, reject) => {
    image.onload = () => resolve(image);
    image.onerror = reject;
  });
  image.src = url;
  return loaded;
}

export async function captureServerCrossSectionCanvas(
  rendererId: string,
): Promise<string | undefined> {
  if (typeof document === 'undefined') return undefined;
  const root = document.querySelector<HTMLElement>(
    `[data-server-cross-section-renderer-id="${rendererId}"]`,
  );
  const raster = root?.querySelector<HTMLCanvasElement>(
    'canvas[data-server-cross-section-raster="1"]',
  );
  if (!root || !raster || raster.width <= 0 || raster.height <= 0) {
    return undefined;
  }

  await nextFrame();

  const rootRect = root.getBoundingClientRect();
  const outputWidth = Math.max(1, Math.round(rootRect.width || raster.width));
  const outputHeight = Math.max(1, Math.round(rootRect.height || raster.height));
  const output = document.createElement('canvas');
  output.width = outputWidth;
  output.height = outputHeight;
  const ctx = output.getContext('2d');
  if (!ctx) return undefined;

  ctx.fillStyle = '#050505';
  ctx.fillRect(0, 0, outputWidth, outputHeight);

  const rasterRect = raster.getBoundingClientRect();
  const rasterX = rasterRect.left - rootRect.left;
  const rasterY = rasterRect.top - rootRect.top;
  ctx.drawImage(
    raster,
    rasterX,
    rasterY,
    Math.max(1, rasterRect.width),
    Math.max(1, rasterRect.height),
  );

  const outlineSvg = root.querySelector<SVGSVGElement>(
    'svg[data-server-cross-section-outline="1"]',
  );
  if (outlineSvg) {
    try {
      const svgRect = outlineSvg.getBoundingClientRect();
      const clone = outlineSvg.cloneNode(true) as SVGSVGElement;
      clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      clone.setAttribute('width', String(Math.max(1, Math.round(svgRect.width))));
      clone.setAttribute('height', String(Math.max(1, Math.round(svgRect.height))));
      clone.removeAttribute('style');
      const svgText = new XMLSerializer().serializeToString(clone);
      const outlineImage = await loadSvgDataUrl(svgText);
      ctx.drawImage(
        outlineImage,
        svgRect.left - rootRect.left,
        svgRect.top - rootRect.top,
        Math.max(1, svgRect.width),
        Math.max(1, svgRect.height),
      );
    } catch {
      // Keep report capture usable even if SVG serialization fails.
    }
  }

  return output.toDataURL('image/png');
}


function clampByte(value: number) {
  if (value <= 0) return 0;
  if (value >= 255) return 255;
  return value | 0;
}

function hexToRgb(hex: string | undefined, fallback: RgbaColor): RgbaColor {
  if (!hex || typeof hex !== 'string') return fallback;
  const normalized = hex.replace('#', '');
  if (normalized.length !== 6) return fallback;
  const value = Number.parseInt(normalized, 16);
  if (!Number.isFinite(value)) return fallback;
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255, fallback[3]];
}

function makeLabelColorTable(config: AppConfig, analysisType: AnalysisType) {
  const colors = config.colors;
  const table: RgbaColor[] = Array.from({ length: 256 }, () => [0, 0, 0, 0]);
  const opacity = (value: number) => clampByte(Math.round(value * 255));

  table[255] = hexToRgb(colors.LUMEN, [212, 212, 56, opacity(0.5)]);

  if (analysisType === 'plaque') {
    table[1] = hexToRgb(colors.lowAttenuation, [255, 60, 60, opacity(0.5)]);
    table[2] = hexToRgb(colors.nonCalcified, [255, 180, 60, opacity(0.5)]);
    table[3] = hexToRgb(colors.CALCIUM, [255, 255, 255, opacity(0.5)]);
  } else if (analysisType === 'fai') {
    table[251] = hexToRgb(colors.FAI4, [170, 0, 255, opacity(0.5)]);
    table[252] = hexToRgb(colors.FAI3, [80, 80, 255, opacity(0.5)]);
    table[253] = hexToRgb(colors.FAI2, [60, 180, 255, opacity(0.5)]);
    table[254] = hexToRgb(colors.FAI1, [60, 255, 120, opacity(0.5)]);
  } else if (analysisType === 'stenosis' || analysisType === 'isr') {
    table[255][3] = opacity(0.5);
  } else if (analysisType === 'aorticValveCalcium' || analysisType === 'tavi') {
    const aortaColors = config.aorta_colors ?? [];
    const aortaOpacity = config.aorta_opacitys ?? [];
    aortaColors.forEach((argb, idx) => {
      if (!argb || argb.length < 4) return;
      const alpha = aortaOpacity[idx]?.[1] ?? argb[0] ?? 0.5;
      table[idx] = [argb[1] ?? 0, argb[2] ?? 0, argb[3] ?? 0, opacity(alpha)];
    });
  } else {
    table[1] = hexToRgb(colors.lowAttenuation, [255, 60, 60, opacity(0.5)]);
    table[2] = hexToRgb(colors.nonCalcified, [255, 180, 60, opacity(0.5)]);
    table[3] = hexToRgb(colors.CALCIUM, [255, 255, 255, opacity(0.5)]);
    table[251] = hexToRgb(colors.FAI4, [170, 0, 255, opacity(0.5)]);
    table[252] = hexToRgb(colors.FAI3, [80, 80, 255, opacity(0.5)]);
    table[253] = hexToRgb(colors.FAI2, [60, 180, 255, opacity(0.5)]);
    table[254] = hexToRgb(colors.FAI1, [60, 255, 120, opacity(0.5)]);
  }

  return table;
}

function worldToIndexMatrix(imageData: vtkImageData) {
  const origin = imageData.getOrigin() as [number, number, number];
  const spacing = imageData.getSpacing() as [number, number, number];
  const direction = imageData.getDirection() as mat3;
  const invDirection = mat3.create();
  if (!mat3.invert(invDirection, direction)) {
    mat3.identity(invDirection);
  }
  return { origin, spacing, invDirection };
}

function isLinearSampleInBounds(
  width: number,
  height: number,
  depth: number,
  i: number,
  j: number,
  k: number,
) {
  return (
    i >= 0 &&
    j >= 0 &&
    k >= 0 &&
    i <= width - 1 &&
    j <= height - 1 &&
    k <= depth - 1
  );
}

function sampleLinearScalar(
  data: ArrayLike<number>,
  width: number,
  height: number,
  depth: number,
  slice: number,
  i: number,
  j: number,
  k: number,
) {
  if (!isLinearSampleInBounds(width, height, depth, i, j, k)) {
    return 0;
  }

  const i0 = Math.floor(i);
  const j0 = Math.floor(j);
  const k0 = Math.floor(k);
  const i1 = i0 + 1 < width ? i0 + 1 : i0;
  const j1 = j0 + 1 < height ? j0 + 1 : j0;
  const k1 = k0 + 1 < depth ? k0 + 1 : k0;
  const tx = i - i0;
  const ty = j - j0;
  const tz = k - k0;

  const k0Base = k0 * slice;
  const k1Base = k1 * slice;
  const j0Base = j0 * width;
  const j1Base = j1 * width;

  const c000 = data[k0Base + j0Base + i0] ?? 0;
  const c100 = data[k0Base + j0Base + i1] ?? 0;
  const c010 = data[k0Base + j1Base + i0] ?? 0;
  const c110 = data[k0Base + j1Base + i1] ?? 0;
  const c001 = data[k1Base + j0Base + i0] ?? 0;
  const c101 = data[k1Base + j0Base + i1] ?? 0;
  const c011 = data[k1Base + j1Base + i0] ?? 0;
  const c111 = data[k1Base + j1Base + i1] ?? 0;

  const c00 = c000 * (1 - tx) + c100 * tx;
  const c10 = c010 * (1 - tx) + c110 * tx;
  const c01 = c001 * (1 - tx) + c101 * tx;
  const c11 = c011 * (1 - tx) + c111 * tx;
  const c0 = c00 * (1 - ty) + c10 * ty;
  const c1 = c01 * (1 - ty) + c11 * ty;
  return c0 * (1 - tz) + c1 * tz;
}

function sampleNearestLabel(
  data: ArrayLike<number>,
  dims: [number, number, number],
  i: number,
  j: number,
  k: number,
) {
  const x = Math.round(i);
  const y = Math.round(j);
  const z = Math.round(k);
  if (
    x < 0 ||
    y < 0 ||
    z < 0 ||
    x >= dims[0] ||
    y >= dims[1] ||
    z >= dims[2]
  ) {
    return 0;
  }
  return data[z * dims[0] * dims[1] + y * dims[0] + x] ?? 0;
}


function worldToContinuousIndex(
  imageData: vtkImageData,
  world: [number, number, number],
): [number, number, number] {
  const { origin, spacing, invDirection } = worldToIndexMatrix(imageData);
  const dx = world[0] - origin[0];
  const dy = world[1] - origin[1];
  const dz = world[2] - origin[2];
  return [
    (dx * invDirection[0] + dy * invDirection[3] + dz * invDirection[6]) /
      (spacing[0] || 1),
    (dx * invDirection[1] + dy * invDirection[4] + dz * invDirection[7]) /
      (spacing[1] || 1),
    (dx * invDirection[2] + dy * invDirection[5] + dz * invDirection[8]) /
      (spacing[2] || 1),
  ];
}

function sampleHuAtSlicePixel({
  imageData,
  axes,
  result,
  pixelX,
  pixelY,
  baseWidth,
}: {
  imageData: vtkImageData;
  axes: mat4;
  result: SliceResult;
  pixelX: number;
  pixelY: number;
  baseWidth: number;
}): number | null {
  const px = Math.min(result.width - 1, Math.max(0, Math.round(pixelX)));
  const py = Math.min(result.height - 1, Math.max(0, Math.round(pixelY)));
  const sampledFromRenderedSlice = result.huValues?.[py * result.width + px];
  if (Number.isFinite(sampledFromRenderedSlice)) {
    return sampledFromRenderedSlice;
  }

  const scalars = imageData.getPointData().getScalars();
  if (!scalars) return null;
  const data = scalars.getData() as ArrayLike<number>;
  if (!data || !data.length) return null;

  const dims = imageData.getDimensions() as [number, number, number];
  const slice = dims[0] * dims[1];
  const spacing = imageData.getSpacing() as [number, number, number];
  const outputScale = Math.max(1e-6, result.sampleScale || 1);
  const safeBaseWidth = Math.max(8, Math.floor(baseWidth));
  const half = Math.floor(safeBaseWidth * 0.5);
  const sx = spacing[0] || 1;
  const sy = spacing[1] || spacing[0] || 1;
  const localStart = -Math.floor(half * sx);
  const localX = localStart + (pixelX / outputScale) * sx;
  const localY = localStart + ((result.height - 1 - pixelY) / outputScale) * sy;

  const world: [number, number, number] = [
    axes[0] * localX + axes[4] * localY + axes[12],
    axes[1] * localX + axes[5] * localY + axes[13],
    axes[2] * localX + axes[6] * localY + axes[14],
  ];
  const [i, j, k] = worldToContinuousIndex(imageData, world);
  if (!isLinearSampleInBounds(dims[0], dims[1], dims[2], i, j, k)) return null;
  const hu = sampleLinearScalar(data, dims[0], dims[1], dims[2], slice, i, j, k);
  return Number.isFinite(hu) ? hu : null;
}

function valueToGray(value: number, policy: ImageWindowingPolicy | undefined, voi: VOI) {
  const windowWidth = Math.max(1, policy?.windowWidth ?? voi.windowWidth ?? 400);
  const windowLevel = policy?.windowLevel ?? voi.windowLevel ?? 40;
  const low = windowLevel - windowWidth / 2;
  const high = windowLevel + windowWidth / 2;
  let gray = ((value - low) / Math.max(1, high - low)) * 255;
  if (policy?.invert ?? voi.invert) gray = 255 - gray;
  return clampByte(Math.round(gray));
}

function blendLabelPixel(
  rgba: Uint8ClampedArray,
  offset: number,
  color: RgbaColor,
) {
  const alpha = color[3];
  if (alpha <= 0) return;
  const inv = 255 - alpha;
  rgba[offset] = clampByte((color[0] * alpha + rgba[offset] * inv) / 255);
  rgba[offset + 1] = clampByte((color[1] * alpha + rgba[offset + 1] * inv) / 255);
  rgba[offset + 2] = clampByte((color[2] * alpha + rgba[offset + 2] * inv) / 255);
  rgba[offset + 3] = 255;
}

function resliceCrossSectionToRgba({
  imageData,
  labelData,
  axes,
  baseWidth,
  upscale,
  voi,
  windowingPolicy,
  slabThicknessMm,
  collectLabels = true,
}: {
  imageData: vtkImageData;
  labelData: vtkImageData;
  axes: mat4;
  baseWidth: number;
  upscale: number;
  voi: VOI;
  windowingPolicy?: ImageWindowingPolicy;
  slabThicknessMm?: number;
  collectLabels?: boolean;
}): SliceResult | null {
  const huScalars = imageData.getPointData().getScalars();
  const labelScalars = labelData.getPointData().getScalars();
  if (!huScalars || !labelScalars) return null;

  const huArray = huScalars.getData() as ArrayLike<number>;
  const labelArray = labelScalars.getData() as ArrayLike<number>;
  const huDims = imageData.getDimensions() as [number, number, number];
  const labelDims = labelData.getDimensions() as [number, number, number];
  const huWidth = huDims[0];
  const huHeight = huDims[1];
  const huDepth = huDims[2];
  const huSlice = huWidth * huHeight;
  const huTransform = worldToIndexMatrix(imageData);
  const labelTransform = worldToIndexMatrix(labelData);
  const huOrigin = huTransform.origin;
  const huSpacing = huTransform.spacing;
  const huInv = huTransform.invDirection;
  const labelOrigin = labelTransform.origin;
  const labelSpacing = labelTransform.spacing;
  const labelInv = labelTransform.invDirection;
  const spacing = imageData.getSpacing() as [number, number, number];
  const shouldCollectLabels = collectLabels !== false;
  const safeBaseWidth = Math.max(8, Math.floor(baseWidth));
  const up = Math.max(0, Math.min(4, Math.floor(upscale)));
  const requestedScale = 1 << up;
  const requestedWidth = safeBaseWidth * requestedScale;
  const maxRenderSize = Math.max(64, SERVER_CROSS_SECTION_MAX_RENDER_SIZE);
  const outputScale = Math.max(
    1,
    Math.min(requestedScale, maxRenderSize / safeBaseWidth),
  );
  const width = Math.max(8, Math.round(safeBaseWidth * outputScale));
  const height = width;
  const capped = width < requestedWidth;
  const pixelCount = width * height;
  const rgba = new Uint8ClampedArray(pixelCount * 4);
  const labels = new Uint8Array(pixelCount);
  const huValues = new Float32Array(pixelCount);
  huValues.fill(Number.NaN);
  const half = Math.floor(safeBaseWidth * 0.5);
  const localStart = -Math.floor(half * (spacing[0] || 1));
  const sx = spacing[0] || 1;
  const sy = spacing[1] || spacing[0] || 1;
  const localStepX = sx / outputScale;

  const xAxis0 = axes[0];
  const xAxis1 = axes[1];
  const xAxis2 = axes[2];
  const yAxis0 = axes[4];
  const yAxis1 = axes[5];
  const yAxis2 = axes[6];
  const zAxis0 = axes[8];
  const zAxis1 = axes[9];
  const zAxis2 = axes[10];
  const origin0 = axes[12];
  const origin1 = axes[13];
  const origin2 = axes[14];

  const makeAffine = (
    inv: mat3,
    origin: [number, number, number],
    spacingValue: number,
    rowOffset: 0 | 1 | 2,
  ) => {
    const r0 = inv[rowOffset];
    const r1 = inv[rowOffset + 3];
    const r2 = inv[rowOffset + 6];
    const denom = spacingValue || 1;
    return {
      base:
        ((origin0 - origin[0]) * r0 +
          (origin1 - origin[1]) * r1 +
          (origin2 - origin[2]) * r2) /
        denom,
      x: (xAxis0 * r0 + xAxis1 * r1 + xAxis2 * r2) / denom,
      y: (yAxis0 * r0 + yAxis1 * r1 + yAxis2 * r2) / denom,
      z: (zAxis0 * r0 + zAxis1 * r1 + zAxis2 * r2) / denom,
    };
  };

  const huAffineI = makeAffine(huInv, huOrigin, huSpacing[0], 0);
  const huAffineJ = makeAffine(huInv, huOrigin, huSpacing[1], 1);
  const huAffineK = makeAffine(huInv, huOrigin, huSpacing[2], 2);
  const labelAffineI = makeAffine(labelInv, labelOrigin, labelSpacing[0], 0);
  const labelAffineJ = makeAffine(labelInv, labelOrigin, labelSpacing[1], 1);
  const labelAffineK = makeAffine(labelInv, labelOrigin, labelSpacing[2], 2);

  const huStepI = huAffineI.x * localStepX;
  const huStepJ = huAffineJ.x * localStepX;
  const huStepK = huAffineK.x * localStepX;
  const labelStepI = labelAffineI.x * localStepX;
  const labelStepJ = labelAffineJ.x * localStepX;
  const labelStepK = labelAffineK.x * localStepX;
  const requestedSlabThicknessMm = Math.max(
    0,
    Number.isFinite(Number(slabThicknessMm)) ? Number(slabThicknessMm) : 0,
  );
  let nativeStepMm = Number.POSITIVE_INFINITY;
  for (let spacingIndex = 0; spacingIndex < spacing.length; spacingIndex += 1) {
    const spacingValue = Math.abs(Number(spacing[spacingIndex]));
    if (Number.isFinite(spacingValue) && spacingValue > 0) {
      nativeStepMm = Math.min(nativeStepMm, spacingValue);
    }
  }
  if (!Number.isFinite(nativeStepMm)) nativeStepMm = 1e-4;
  nativeStepMm = Math.max(1e-4, nativeStepMm);
  const slabSampleCount =
    requestedSlabThicknessMm <= SERVER_CROSS_SECTION_SLAB_EPSILON_MM
      ? 1
      : Math.max(2, Math.ceil(requestedSlabThicknessMm / nativeStepMm) + 1);
  const slabHuOffsetI = new Float64Array(slabSampleCount);
  const slabHuOffsetJ = new Float64Array(slabSampleCount);
  const slabHuOffsetK = new Float64Array(slabSampleCount);
  const slabLabelOffsetI = shouldCollectLabels
    ? new Float64Array(slabSampleCount)
    : null;
  const slabLabelOffsetJ = shouldCollectLabels
    ? new Float64Array(slabSampleCount)
    : null;
  const slabLabelOffsetK = shouldCollectLabels
    ? new Float64Array(slabSampleCount)
    : null;
  for (let slabIndex = 0; slabIndex < slabSampleCount; slabIndex += 1) {
    const slabMm =
      slabSampleCount <= 1
        ? 0
        : -0.5 * requestedSlabThicknessMm +
          (requestedSlabThicknessMm * slabIndex) / (slabSampleCount - 1);
    slabHuOffsetI[slabIndex] = huAffineI.z * slabMm;
    slabHuOffsetJ[slabIndex] = huAffineJ.z * slabMm;
    slabHuOffsetK[slabIndex] = huAffineK.z * slabMm;
    if (slabLabelOffsetI && slabLabelOffsetJ && slabLabelOffsetK) {
      slabLabelOffsetI[slabIndex] = labelAffineI.z * slabMm;
      slabLabelOffsetJ[slabIndex] = labelAffineJ.z * slabMm;
      slabLabelOffsetK[slabIndex] = labelAffineK.z * slabMm;
    }
  }
  const hasSlabLabelSampling =
    shouldCollectLabels &&
    slabLabelOffsetI !== null &&
    slabLabelOffsetJ !== null &&
    slabLabelOffsetK !== null;
  const labelWidth = labelDims[0];
  const labelHeight = labelDims[1];
  const labelDepth = labelDims[2];
  const labelSlice = labelWidth * labelHeight;
  const windowWidth = Math.max(1, windowingPolicy?.windowWidth ?? voi.windowWidth ?? 400);
  const windowLevel = windowingPolicy?.windowLevel ?? voi.windowLevel ?? 40;
  const windowLow = windowLevel - windowWidth / 2;
  const windowScale = 255 / Math.max(1, windowWidth);
  const invertWindow = windowingPolicy?.invert ?? voi.invert;

  for (let y = 0; y < height; y += 1) {
    const localY = localStart + ((height - 1 - y) / outputScale) * sy;
    let huI = huAffineI.base + huAffineI.x * localStart + huAffineI.y * localY;
    let huJ = huAffineJ.base + huAffineJ.x * localStart + huAffineJ.y * localY;
    let huK = huAffineK.base + huAffineK.x * localStart + huAffineK.y * localY;
    let labelI =
      labelAffineI.base + labelAffineI.x * localStart + labelAffineI.y * localY;
    let labelJ =
      labelAffineJ.base + labelAffineJ.x * localStart + labelAffineJ.y * localY;
    let labelK =
      labelAffineK.base + labelAffineK.x * localStart + labelAffineK.y * localY;
    let labelOffset = y * width;
    let rgbaOffset = labelOffset * 4;

    for (let x = 0; x < width; x += 1) {
      let accumulatedHu = 0;
      let chosenLabel = 0;
      let validHuSampleCount = 0;

      if (slabSampleCount === 1) {
        if (isLinearSampleInBounds(huWidth, huHeight, huDepth, huI, huJ, huK)) {
          accumulatedHu = sampleLinearScalar(
            huArray,
            huWidth,
            huHeight,
            huDepth,
            huSlice,
            huI,
            huJ,
            huK,
          );
          validHuSampleCount = 1;
        }

        if (shouldCollectLabels) {
          const lx = Math.round(labelI);
          const ly = Math.round(labelJ);
          const lz = Math.round(labelK);
          if (
            lx >= 0 &&
            ly >= 0 &&
            lz >= 0 &&
            lx < labelWidth &&
            ly < labelHeight &&
            lz < labelDepth
          ) {
            chosenLabel = Number(
              labelArray[lz * labelSlice + ly * labelWidth + lx] ?? 0,
            );
          }
        }
      } else if (hasSlabLabelSampling) {
        for (let slabIndex = 0; slabIndex < slabSampleCount; slabIndex += 1) {
          const sampleI = huI + slabHuOffsetI[slabIndex];
          const sampleJ = huJ + slabHuOffsetJ[slabIndex];
          const sampleK = huK + slabHuOffsetK[slabIndex];
          if (
            isLinearSampleInBounds(
              huWidth,
              huHeight,
              huDepth,
              sampleI,
              sampleJ,
              sampleK,
            )
          ) {
            accumulatedHu += sampleLinearScalar(
              huArray,
              huWidth,
              huHeight,
              huDepth,
              huSlice,
              sampleI,
              sampleJ,
              sampleK,
            );
            validHuSampleCount += 1;
          }

          const lx = Math.round(labelI + slabLabelOffsetI![slabIndex]);
          const ly = Math.round(labelJ + slabLabelOffsetJ![slabIndex]);
          const lz = Math.round(labelK + slabLabelOffsetK![slabIndex]);
          if (
            lx >= 0 &&
            ly >= 0 &&
            lz >= 0 &&
            lx < labelWidth &&
            ly < labelHeight &&
            lz < labelDepth
          ) {
            const labelValue = Number(
              labelArray[lz * labelSlice + ly * labelWidth + lx] ?? 0,
            );
            if (labelValue > chosenLabel) chosenLabel = labelValue;
          }
        }
        if (validHuSampleCount > 0) {
          accumulatedHu /= validHuSampleCount;
        }
      } else {
        for (let slabIndex = 0; slabIndex < slabSampleCount; slabIndex += 1) {
          const sampleI = huI + slabHuOffsetI[slabIndex];
          const sampleJ = huJ + slabHuOffsetJ[slabIndex];
          const sampleK = huK + slabHuOffsetK[slabIndex];
          if (
            isLinearSampleInBounds(
              huWidth,
              huHeight,
              huDepth,
              sampleI,
              sampleJ,
              sampleK,
            )
          ) {
            accumulatedHu += sampleLinearScalar(
              huArray,
              huWidth,
              huHeight,
              huDepth,
              huSlice,
              sampleI,
              sampleJ,
              sampleK,
            );
            validHuSampleCount += 1;
          }
        }
        if (validHuSampleCount > 0) {
          accumulatedHu /= validHuSampleCount;
        }
      }

      if (validHuSampleCount <= 0) {
        rgba[rgbaOffset] = 0;
        rgba[rgbaOffset + 1] = 0;
        rgba[rgbaOffset + 2] = 0;
        rgba[rgbaOffset + 3] = 255;
        labels[labelOffset] = 0;
        huValues[labelOffset] = Number.NaN;
      } else {
        huValues[labelOffset] = Number.isFinite(accumulatedHu) ? accumulatedHu : Number.NaN;
        let grayValue = (accumulatedHu - windowLow) * windowScale;
        if (invertWindow) grayValue = 255 - grayValue;
        const gray = clampByte(Math.round(grayValue));
        rgba[rgbaOffset] = gray;
        rgba[rgbaOffset + 1] = gray;
        rgba[rgbaOffset + 2] = gray;
        rgba[rgbaOffset + 3] = 255;
        labels[labelOffset] = chosenLabel;
      }

      rgbaOffset += 4;
      labelOffset += 1;
      huI += huStepI;
      huJ += huStepJ;
      huK += huStepK;
      labelI += labelStepI;
      labelJ += labelStepJ;
      labelK += labelStepK;
    }
  }

  return {
    width,
    height,
    rgba,
    labels,
    huValues,
    sampleScale: outputScale,
    requestedWidth,
    capped,
  };
}


function makePointKey(x: number, y: number) {
  return `${x},${y}`;
}

function parsePointKey(key: string): Point2D {
  const splitAt = key.indexOf(',');
  return [Number(key.slice(0, splitAt)), Number(key.slice(splitAt + 1))];
}

type BoundaryEdge = {
  startKey: string;
  endKey: string;
  end: Point2D;
};

function makeEdgeKey(startKey: string, endKey: string) {
  return `${startKey}->${endKey}`;
}

function buildContourLoops(
  labels: Uint8Array,
  width: number,
  height: number,
  values: number[],
): Point2D[][] {
  const table = new Uint8Array(256);
  values.forEach((value) => {
    const v = value | 0;
    if (v >= 0 && v <= 255) table[v] = 1;
  });

  const outgoing = new Map<string, BoundaryEdge[]>();
  const edgeKeys: string[] = [];

  const addEdge = (x0: number, y0: number, x1: number, y1: number) => {
    const startKey = makePointKey(x0, y0);
    const endKey = makePointKey(x1, y1);
    const edge: BoundaryEdge = {
      startKey,
      endKey,
      end: [x1, y1],
    };
    const key = makeEdgeKey(startKey, endKey);
    edgeKeys.push(key);
    const bucket = outgoing.get(startKey);
    if (bucket) bucket.push(edge);
    else outgoing.set(startKey, [edge]);
  };

  for (let y = 0; y < height; y += 1) {
    const row = y * width;
    const rowAbove = row - width;
    const rowBelow = row + width;
    for (let x = 0; x < width; x += 1) {
      const offset = row + x;
      if (!table[labels[offset]]) continue;

      const topEmpty = y === 0 || !table[labels[rowAbove + x]];
      const rightEmpty = x === width - 1 || !table[labels[offset + 1]];
      const bottomEmpty = y === height - 1 || !table[labels[rowBelow + x]];
      const leftEmpty = x === 0 || !table[labels[offset - 1]];

      if (topEmpty) addEdge(x, y, x + 1, y);
      if (rightEmpty) addEdge(x + 1, y, x + 1, y + 1);
      if (bottomEmpty) addEdge(x + 1, y + 1, x, y + 1);
      if (leftEmpty) addEdge(x, y + 1, x, y);
    }
  }

  const visited = new Set<string>();
  const loops: Point2D[][] = [];

  for (const seedKey of edgeKeys) {
    if (visited.has(seedKey)) continue;
    const arrowAt = seedKey.indexOf('->');
    const startKey = seedKey.slice(0, arrowAt);
    const firstEndKey = seedKey.slice(arrowAt + 2);
    const startPoint = parsePointKey(startKey);
    const firstEndPoint = parsePointKey(firstEndKey);
    const loop: Point2D[] = [startPoint, firstEndPoint];

    visited.add(seedKey);
    let currentKey = firstEndKey;
    let guard = 0;
    const guardLimit = Math.max(16, edgeKeys.length + 8);

    while (currentKey !== startKey && guard < guardLimit) {
      guard += 1;
      const candidates = outgoing.get(currentKey);
      if (!candidates?.length) break;
      let next: BoundaryEdge | null = null;
      for (let i = 0; i < candidates.length; i += 1) {
        const candidate = candidates[i];
        const candidateKey = makeEdgeKey(candidate.startKey, candidate.endKey);
        if (!visited.has(candidateKey)) {
          next = candidate;
          visited.add(candidateKey);
          break;
        }
      }
      if (!next) break;
      loop.push(next.end);
      currentKey = next.endKey;
    }

    if (loop.length >= SERVER_CROSS_SECTION_OUTLINE_MIN_CONTOUR_POINTS) {
      const first = loop[0];
      const last = loop[loop.length - 1];
      if (first[0] !== last[0] || first[1] !== last[1]) {
        loop.push([first[0], first[1]]);
      }
      loops.push(loop);
    }
  }

  return loops;
}

function removeCollinearClosedPoints(points: Point2D[]): Point2D[] {
  if (points.length < 4) return points;
  const closed = points[0][0] === points[points.length - 1][0] &&
    points[0][1] === points[points.length - 1][1];
  const source = closed ? points.slice(0, -1) : points;
  const reduced: Point2D[] = [];

  for (let i = 0; i < source.length; i += 1) {
    const prev = source[(i - 1 + source.length) % source.length];
    const curr = source[i];
    const next = source[(i + 1) % source.length];
    const dx1 = curr[0] - prev[0];
    const dy1 = curr[1] - prev[1];
    const dx2 = next[0] - curr[0];
    const dy2 = next[1] - curr[1];
    if (dx1 === dx2 && dy1 === dy2) continue;
    reduced.push([curr[0], curr[1]]);
  }

  if (reduced.length >= 2) reduced.push([reduced[0][0], reduced[0][1]]);
  return reduced;
}

function smoothClosedContour(points: Point2D[]): Point2D[] {
  let current = removeCollinearClosedPoints(points);
  if (current.length < 4) return current;

  for (let iter = 0; iter < SERVER_CROSS_SECTION_OUTLINE_SMOOTHING_ITERATIONS; iter += 1) {
    const source = current.slice(0, -1);
    if (source.length < 3) break;
    const next: Point2D[] = [];
    for (let i = 0; i < source.length; i += 1) {
      const p0 = source[i];
      const p1 = source[(i + 1) % source.length];
      next.push([
        p0[0] * 0.75 + p1[0] * 0.25,
        p0[1] * 0.75 + p1[1] * 0.25,
      ]);
      next.push([
        p0[0] * 0.25 + p1[0] * 0.75,
        p0[1] * 0.25 + p1[1] * 0.75,
      ]);
    }
    next.push([next[0][0], next[0][1]]);
    current = next;
  }

  return current;
}

function contourToPath(points: Point2D[]) {
  if (points.length < SERVER_CROSS_SECTION_OUTLINE_MIN_CONTOUR_POINTS) return '';
  const smoothed = smoothClosedContour(points);
  if (smoothed.length < SERVER_CROSS_SECTION_OUTLINE_MIN_CONTOUR_POINTS) return '';
  let d = `M ${smoothed[0][0].toFixed(2)} ${smoothed[0][1].toFixed(2)}`;
  for (let i = 1; i < smoothed.length; i += 1) {
    d += ` L ${smoothed[i][0].toFixed(2)} ${smoothed[i][1].toFixed(2)}`;
  }
  return `${d} Z`;
}

function buildOutlinePathData(
  result: SliceResult,
  analysisType: AnalysisType,
): OutlinePathData {
  const lumenLoops = buildContourLoops(result.labels, result.width, result.height, [255]);
  const plaqueLoops = analysisType === 'plaque'
    ? buildContourLoops(result.labels, result.width, result.height, [1, 2, 3, 255])
    : [];

  return {
    width: result.width,
    height: result.height,
    lumen: lumenLoops.map(contourToPath).filter(Boolean),
    plaque: plaqueLoops.map(contourToPath).filter(Boolean),
  };
}

function calculateWindowLevelFromDrag({
  current,
  deltaX,
  deltaY,
}: {
  current?: ImageWindowingPolicy;
  deltaX: number;
  deltaY: number;
}): ImageWindowingPolicy | null {
  if (!current) return null;
  return {
    ...current,
    windowLevel: current.windowLevel + deltaY,
    windowWidth: Math.max(1, current.windowWidth + deltaX),
  };
}

export default function ServerCrossSectionCanvas({
  className = '',
  imageData,
  labelData,
  axes,
  index,
  analysisType,
  voi,
  config,
  baseWidth,
  upscale,
  activeToolId,
  visibility,
  windowingPolicy,
  onWheelIndex,
  onScrollDragIndex,
  onWindowLevelChange,
  rendererId,
  initialZoomScale,
  slabThicknessMm,
  onHoverHU,
}: ServerCrossSectionCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dragRef = useRef<{
    kind: DragKind;
    clientX: number;
    clientY: number;
    offsetX: number;
    offsetY: number;
  } | null>(null);
  const liveWindowingPolicyRef = useRef<ImageWindowingPolicy | undefined>(
    windowingPolicy,
  );
  const [interactionWindowingPolicy, setInteractionWindowingPolicy] = useState<
    ImageWindowingPolicy | undefined
  >(undefined);
  const windowingPreviewRafRef = useRef<number | null>(null);
  const pendingWindowingPreviewRef = useRef<ImageWindowingPolicy | null>(null);
  const effectiveWindowingPolicy = interactionWindowingPolicy ?? windowingPolicy;
  const sliceRef = useRef<SliceResult | null>(null);
  const displayBufferRef = useRef<Uint8ClampedArray | null>(null);
  const [containerSize, setContainerSize] = useState({ width: 1, height: 1 });
  const [viewportTransform, setViewportTransform] = useState<ViewportTransform>(() => ({
    scale: initialZoomScale ?? getInitialViewportScale(analysisType),
    offsetX: 0,
    offsetY: 0,
  }));
  const [geometry, setGeometry] = useState<ImageGeometry>(EMPTY_GEOMETRY);
  const [outlinePathData, setOutlinePathData] = useState<OutlinePathData | null>(null);

  useEffect(() => {
    const nextScale = initialZoomScale ?? getInitialViewportScale(analysisType);
    setViewportTransform((prev) => ({
      ...prev,
      scale: clampViewportScale(nextScale),
      offsetX: 0,
      offsetY: 0,
    }));
  }, [analysisType, initialZoomScale]);

  useEffect(() => {
    if (dragRef.current?.kind === 'window-level') return;
    liveWindowingPolicyRef.current = windowingPolicy;
    setInteractionWindowingPolicy(undefined);
  }, [windowingPolicy]);

  const showLumen = visibility?.lumen !== false;
  const showLumenOutline = visibility?.lumenoutline !== false;
  const showPlaque = visibility?.plaque !== false;
  const showPlaqueOutline = visibility?.plaqueoutline !== false;
  const shouldBuildOutlinePaths =
    showLumenOutline || (analysisType === 'plaque' && showPlaqueOutline);
  const showFai = visibility?.fai !== false;
  const showAorta = visibility?.aorta !== false;
  const showAvc = visibility?.avc !== false;
  const labelColors = useMemo(() => {
    const table = makeLabelColorTable(config, analysisType).map(
      (color) => [...color] as RgbaColor,
    );
    if (!showLumen) table[255][3] = 0;
    if (!showPlaque) {
      table[1][3] = 0;
      table[2][3] = 0;
      table[3][3] = 0;
    }
    if (!showFai) {
      table[251][3] = 0;
      table[252][3] = 0;
      table[253][3] = 0;
      table[254][3] = 0;
    }
    if (!showAorta || !showAvc) {
      const avcValues = new Set(config.avc_indexs ?? []);
      const aortaValues = new Set(config.aorta_indexs ?? []);
      if (!showAorta) {
        aortaValues.forEach((value) => {
          if (!avcValues.has(value) && table[value]) table[value][3] = 0;
        });
      }
      if (!showAvc) {
        avcValues.forEach((value) => {
          if (table[value]) table[value][3] = 0;
        });
      }
    }
    return table;
  }, [
    analysisType,
    config,
    showAorta,
    showAvc,
    showFai,
    showLumen,
    showPlaque,
  ]);
  const showLabelFill = labelColors.some((color) => color[3] > 0);
  const drawOptionsRef = useRef({
    analysisType,
    index,
    labelColors,
    lumenOutlineColor: config.colors.lumenoutline ?? '#d4d438',
    wallOutlineColor: config.colors.walloutline ?? '#d87409',
    showLabelFill,
    showLumenOutline,
    showPlaqueOutline,
  });

  useLayoutEffect(() => {
    drawOptionsRef.current = {
      analysisType,
      index,
      labelColors,
      lumenOutlineColor: config.colors.lumenoutline ?? '#d4d438',
      wallOutlineColor: config.colors.walloutline ?? '#d87409',
      showLabelFill,
      showLumenOutline,
      showPlaqueOutline,
    };
  }, [
    analysisType,
    config.colors.lumenoutline,
    config.colors.walloutline,
    index,
    labelColors,
    showLabelFill,
    showLumenOutline,
    showPlaqueOutline,
  ]);

  const drawSliceToCanvas = useCallback(
    (sliceOverride?: SliceResult | null, reason = 'draw') => {
      const canvas = canvasRef.current;
      const result = sliceOverride ?? sliceRef.current;
      if (!canvas || !result) return;

      const options = drawOptionsRef.current;
      const drawStart = performance.now();
      if (canvas.width !== result.width) canvas.width = result.width;
      if (canvas.height !== result.height) canvas.height = result.height;
      const ctx = canvas.getContext('2d', { alpha: true });
      if (!ctx) return;

      let composeMs = 0;
      let putMs = 0;
      let plaqueOutlineMs = 0;
      let lumenOutlineMs = 0;

      let pixels = result.rgba;
      if (options.showLabelFill) {
        const composeStart = performance.now();
        const requiredLength = result.rgba.length;
        let displayBuffer = displayBufferRef.current;
        if (!displayBuffer || displayBuffer.length !== requiredLength) {
          displayBuffer = new Uint8ClampedArray(requiredLength);
          displayBufferRef.current = displayBuffer;
        }
        displayBuffer.set(result.rgba);
        const labels = result.labels;
        for (let i = 0; i < labels.length; i += 1) {
          const color = options.labelColors[labels[i]];
          if (!color || color[3] <= 0) continue;
          blendLabelPixel(displayBuffer, i * 4, color);
        }
        pixels = displayBuffer;
        composeMs = performance.now() - composeStart;
      }

      const putStart = performance.now();
      ctx.putImageData(new ImageData(pixels, result.width, result.height), 0, 0);
      putMs = performance.now() - putStart;

      // Outlines are drawn as SVG vector paths above the raster canvas.
      // Keep raster redraws focused on HU/label pixels so S visibility toggles
      // do not rebuild boundary segment arrays on every key press.

      const totalMs = performance.now() - drawStart;
      if (totalMs >= SERVER_CROSS_SECTION_PERF_LOG_THRESHOLD_MS) {
        logServerCrossSectionPerf('draw', {
          reason,
          index: options.index,
          analysisType: options.analysisType,
          width: result.width,
          height: result.height,
          totalMs: Math.round(totalMs * 10) / 10,
          composeMs: Math.round(composeMs * 10) / 10,
          putMs: Math.round(putMs * 10) / 10,
          plaqueOutlineMs: Math.round(plaqueOutlineMs * 10) / 10,
          lumenOutlineMs: Math.round(lumenOutlineMs * 10) / 10,
          showLabelFill: options.showLabelFill,
          showLumenOutline: options.showLumenOutline,
          showPlaqueOutline: options.showPlaqueOutline,
        });
      }
    },
    [],
  );

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (!rect) return;
      setContainerSize({
        width: Math.max(1, rect.width),
        height: Math.max(1, rect.height),
      });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    if (!canvasRef.current || !imageData || !labelData || !axes) return;
    const totalStart = performance.now();
    const result = resliceCrossSectionToRgba({
      imageData,
      labelData,
      axes,
      baseWidth,
      upscale,
      voi,
      windowingPolicy: effectiveWindowingPolicy,
      slabThicknessMm,
      collectLabels: showLabelFill || shouldBuildOutlinePaths,
    });
    const resliceMs = performance.now() - totalStart;
    if (!result) return;

    sliceRef.current = result;
    const outlineStart = performance.now();
    const nextOutlinePathData = shouldBuildOutlinePaths
      ? buildOutlinePathData(result, analysisType)
      : null;
    const outlinePathMs = performance.now() - outlineStart;
    setOutlinePathData(nextOutlinePathData);
    drawSliceToCanvas(result, 'reslice');

    const totalMs = performance.now() - totalStart;
    if (totalMs >= SERVER_CROSS_SECTION_PERF_LOG_THRESHOLD_MS) {
      logServerCrossSectionPerf('reslice', {
        index,
        analysisType,
        width: result.width,
        height: result.height,
        totalMs: Math.round(totalMs * 10) / 10,
        resliceMs: Math.round(resliceMs * 10) / 10,
        drawMs: Math.round((totalMs - resliceMs - outlinePathMs) * 10) / 10,
        outlinePathMs: Math.round(outlinePathMs * 10) / 10,
        upscale,
        sampleScale: Math.round(result.sampleScale * 100) / 100,
        requestedWidth: result.requestedWidth,
        capped: result.capped,
        slabThicknessMm: Math.round((slabThicknessMm ?? 0) * 10) / 10,
      });
    }

    setGeometry((prev) =>
      prev.width === result.width && prev.height === result.height
        ? prev
        : { width: result.width, height: result.height },
    );
  }, [
    analysisType,
    axes,
    baseWidth,
    imageData,
    index,
    labelData,
    upscale,
    voi,
    effectiveWindowingPolicy,
    slabThicknessMm,
    shouldBuildOutlinePaths,
    showLabelFill,
  ]);

  useLayoutEffect(() => {
    drawSliceToCanvas(null, 'visibility');
  }, [
    analysisType,
    config.colors.lumenoutline,
    config.colors.walloutline,
    drawSliceToCanvas,
    labelColors,
    showLabelFill,
    showLumenOutline,
    showPlaqueOutline,
  ]);

  const fitRect = useMemo(
    () =>
      calculateImageFitRect(
        'contain',
        containerSize.width,
        containerSize.height,
        geometry.width,
        geometry.height,
      ),
    [containerSize.height, containerSize.width, geometry.height, geometry.width],
  );

  const stageStyle = useMemo(
    () => ({
      left: fitRect.left,
      top: fitRect.top,
      width: fitRect.width,
      height: fitRect.height,
      transform: `translate(${viewportTransform.offsetX}px, ${viewportTransform.offsetY}px) scale(${viewportTransform.scale})`,
      transformOrigin: 'center center',
    }),
    [fitRect, viewportTransform],
  );

  const zoomAtCenter = useCallback((scaleFactor: number) => {
    setViewportTransform((prev) => ({
      ...prev,
      scale: clampViewportScale(prev.scale * scaleFactor),
    }));
  }, []);

  const resolveDragKind = (event: React.PointerEvent<HTMLDivElement>): DragKind | null => {
    if (event.buttons === 3) return 'zoom';
    if (event.button === 1 || event.buttons === 4) return 'window-level';
    if (event.button === 2) return 'pan';
    if (event.button !== 0) return null;
    if (activeToolId === 'Pan') return 'pan';
    if (activeToolId === 'Zoom') return 'zoom';
    if (activeToolId === 'Scroll') return 'scroll';
    if (activeToolId === 'WindowLevel') return 'window-level';
    return null;
  };

  const handleNativeWheel = useCallback(
    (event: WheelEvent) => {
      if (event.cancelable) event.preventDefault();
      event.stopPropagation();

      if (event.ctrlKey || activeToolId === 'Zoom') {
        zoomAtCenter(Math.exp(-event.deltaY * 0.0015));
        return;
      }

      const indexStep = getWheelIndexStep(event.deltaY);
      if (indexStep !== 0) onWheelIndex?.(indexStep);
    },
    [activeToolId, onWheelIndex, zoomAtCenter],
  );

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    element.addEventListener('wheel', handleNativeWheel, { passive: false });
    return () => {
      element.removeEventListener('wheel', handleNativeWheel);
    };
  }, [handleNativeWheel]);

  const handleHoverPointerMove: React.PointerEventHandler<HTMLDivElement> = (event) => {
    if (!onHoverHU) return;
    const canvas = canvasRef.current;
    const result = sliceRef.current;
    if (!canvas || !result || !imageData || !axes) {
      onHoverHU(null);
      return;
    }
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) {
      onHoverHU(null);
      return;
    }
    const normalizedX = (event.clientX - rect.left) / rect.width;
    const normalizedY = (event.clientY - rect.top) / rect.height;
    if (normalizedX < 0 || normalizedX > 1 || normalizedY < 0 || normalizedY > 1) {
      onHoverHU(null);
      return;
    }
    const pixelX = Math.min(
      result.width - 1,
      Math.max(0, normalizedX * result.width),
    );
    const pixelY = Math.min(
      result.height - 1,
      Math.max(0, normalizedY * result.height),
    );
    onHoverHU(
      sampleHuAtSlicePixel({
        imageData,
        axes,
        result,
        pixelX,
        pixelY,
        baseWidth,
      }),
    );
  };

  const handleHoverPointerLeave: React.PointerEventHandler<HTMLDivElement> = () => {
    onHoverHU?.(null);
  };

  const flushWindowingPreview = useCallback(() => {
    windowingPreviewRafRef.current = null;
    const pending = pendingWindowingPreviewRef.current;
    pendingWindowingPreviewRef.current = null;
    if (!pending) return;
    setInteractionWindowingPolicy(pending);
    onWindowLevelChange?.(pending);
  }, [onWindowLevelChange]);

  const scheduleWindowingPreview = useCallback(
    (next: ImageWindowingPolicy) => {
      pendingWindowingPreviewRef.current = next;
      if (windowingPreviewRafRef.current !== null) return;
      windowingPreviewRafRef.current = window.requestAnimationFrame(
        flushWindowingPreview,
      );
    },
    [flushWindowingPreview],
  );

  useEffect(
    () => () => {
      if (windowingPreviewRafRef.current !== null) {
        window.cancelAnimationFrame(windowingPreviewRafRef.current);
        windowingPreviewRafRef.current = null;
      }
      pendingWindowingPreviewRef.current = null;
    },
    [],
  );

  const handlePointerDown: React.PointerEventHandler<HTMLDivElement> = (event) => {
    const kind = resolveDragKind(event);
    if (!kind) return;
    event.preventDefault();
    event.stopPropagation();
    dragRef.current = {
      kind,
      clientX: event.clientX,
      clientY: event.clientY,
      offsetX: viewportTransform.offsetX,
      offsetY: viewportTransform.offsetY,
    };

    const handlePointerMove = (moveEvent: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      const deltaX = moveEvent.clientX - drag.clientX;
      const deltaY = moveEvent.clientY - drag.clientY;
      if (drag.kind === 'pan') {
        setViewportTransform((prev) => ({
          ...prev,
          offsetX: drag.offsetX + deltaX,
          offsetY: drag.offsetY + deltaY,
        }));
        return;
      }
      if (drag.kind === 'zoom') {
        if (deltaY !== 0) {
          zoomAtCenter(Math.exp(-deltaY * 0.01));
        }
        drag.clientX = moveEvent.clientX;
        drag.clientY = moveEvent.clientY;
        return;
      }
      if (drag.kind === 'scroll') {
        if (Math.abs(deltaY) >= 1) {
          onScrollDragIndex?.(deltaY > 0 ? 1 : -1);
          drag.clientX = moveEvent.clientX;
          drag.clientY = moveEvent.clientY;
        }
        return;
      }
      if (drag.kind === 'window-level') {
        const next = calculateWindowLevelFromDrag({
          current: liveWindowingPolicyRef.current ?? windowingPolicy,
          deltaX,
          deltaY,
        });
        if (next) {
          liveWindowingPolicyRef.current = next;
          publishImageLayerStackLiveWindowingPolicy(
            next,
            `server-cross-section-${rendererId}`,
          );
          scheduleWindowingPreview(next);
        }
        drag.clientX = moveEvent.clientX;
        drag.clientY = moveEvent.clientY;
      }
    };

    const handlePointerUp = () => {
      if (windowingPreviewRafRef.current !== null) {
        window.cancelAnimationFrame(windowingPreviewRafRef.current);
        flushWindowingPreview();
      }
      dragRef.current = null;
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };

    window.addEventListener('pointermove', handlePointerMove, { passive: false });
    window.addEventListener('pointerup', handlePointerUp, { passive: true });
    window.addEventListener('pointercancel', handlePointerUp, { passive: true });
  };

  return (
    <div
      ref={containerRef}
      className={`server-cross-section-canvas ${className}`}
      data-server-cross-section-canvas="1"
      data-server-cross-section-renderer-id={rendererId}
      data-index={index}
      onPointerDown={handlePointerDown}
      onPointerMove={handleHoverPointerMove}
      onPointerLeave={handleHoverPointerLeave}
      onContextMenu={(event) => event.preventDefault()}
      style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        background: '#050505',
        touchAction: 'none',
      }}
    >
      {!imageData || !labelData || !axes ? (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'rgba(255,255,255,0.65)',
            fontSize: '12px',
          }}
        >
          Cross-section resource loading
        </div>
      ) : (
        <>
          <canvas
            ref={canvasRef}
            data-server-cross-section-raster="1"
            style={{
              position: 'absolute',
              left: stageStyle.left,
              top: stageStyle.top,
              width: stageStyle.width,
              height: stageStyle.height,
              transform: stageStyle.transform,
              transformOrigin: stageStyle.transformOrigin,
              willChange: 'transform',
              imageRendering: 'auto',
            }}
          />
          {outlinePathData && outlinePathData.width === geometry.width && outlinePathData.height === geometry.height ? (
            <svg
              data-server-cross-section-outline="1"
              viewBox={`0 0 ${outlinePathData.width} ${outlinePathData.height}`}
              preserveAspectRatio="none"
              style={{
                position: 'absolute',
                left: stageStyle.left,
                top: stageStyle.top,
                width: stageStyle.width,
                height: stageStyle.height,
                transform: stageStyle.transform,
                transformOrigin: stageStyle.transformOrigin,
                willChange: 'transform',
                overflow: 'visible',
                pointerEvents: 'none',
              }}
            >
              {showPlaqueOutline && analysisType === 'plaque'
                ? outlinePathData.plaque.map((d, pathIndex) => (
                    <path
                      // eslint-disable-next-line react/no-array-index-key
                      key={`plaque-${pathIndex}`}
                      d={d}
                      fill="none"
                      stroke={config.colors.walloutline ?? '#d87409'}
                      strokeWidth={SERVER_CROSS_SECTION_OUTLINE_STROKE_WIDTH}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      vectorEffect="non-scaling-stroke"
                    />
                  ))
                : null}
              {showLumenOutline
                ? outlinePathData.lumen.map((d, pathIndex) => (
                    <path
                      // eslint-disable-next-line react/no-array-index-key
                      key={`lumen-${pathIndex}`}
                      d={d}
                      fill="none"
                      stroke={config.colors.lumenoutline ?? '#d4d438'}
                      strokeWidth={SERVER_CROSS_SECTION_OUTLINE_STROKE_WIDTH}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      vectorEffect="non-scaling-stroke"
                    />
                  ))
                : null}
            </svg>
          ) : null}
        </>
      )}
    </div>
  );
}
